import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import { ApiError, type ApiEnvelope } from '@/types/api';
import type { CouponApplyResult, CouponCartItem, CouponValidation } from '@/types/coupon';

/**
 * POST /coupon/validate { code }. On an invalid coupon the BE returns a 4xx
 * with an `errorCode`; we surface that as a structured `CouponValidation`
 * with `valid: false` so the UI can render per-case copy without a throw.
 */
export async function validateCoupon(code: string): Promise<CouponValidation> {
  try {
    const res = await http.post<ApiEnvelope<CouponValidation>>('/coupon/validate', { code });
    const data = unwrap(res.data);
    return { ...data, code, valid: data.valid ?? true };
  } catch (error) {
    const err = toApiError(error);
    if (err instanceof ApiError && err.code >= 400 && err.code < 500) {
      return {
        code,
        valid: false,
        message: err.message,
        errorCode: err.errorCode ?? undefined,
      };
    }
    throw err;
  }
}

/**
 * POST /coupon/apply { code, userId, cart } — the ONLY endpoint that returns a
 * money amount.
 *
 * `/coupon/validate` answers "does this code exist and is it usable", and
 * nothing more: no `discountAmount`. The field was read straight off the
 * validate response, so it was always `undefined` — `cartSelectors.couponSavings`
 * fell back to 0, the total never moved and the "Hemat …" line never rendered,
 * on an otherwise perfectly accepted coupon.
 *
 * Applying needs the cart because that is what the amount is computed from
 * (a percentage of `totalAmount`, a cart-scope `minPurchaseAmount` gate, …).
 * This is a PREVIEW — nothing is persisted, and the sale re-validates
 * authoritatively from the `couponCode` on the payload.
 *
 * `visitDate` is the day the sale will carry. Without it the backend cannot
 * judge a discount limited to a visit-date window, so a coupon for 1–10 April
 * read as applied for 13 April and then failed at payment.
 */
export async function applyCoupon(
  code: string,
  cart: { items: CouponCartItem[]; totalAmount: number },
  visitDate?: string,
): Promise<CouponValidation> {
  try {
    const res = await http.post<ApiEnvelope<CouponApplyResult>>('/coupon/apply', {
      code,
      userId: null,
      cart,
      ...(visitDate ? { visitDate } : {}),
    });
    const data = unwrap(res.data);
    const d = data?.discount ?? {};
    // The engine breakdown carries the number under several names depending on
    // which arm produced it; `summary` is the stacked-discount shape.
    const discountAmount =
      d.totalDiscountAmount ?? d.savings ?? d.summary?.totalDiscountAmount ?? 0;
    return {
      code,
      valid: discountAmount > 0,
      discountAmount,
      discountType: d.appliedDiscounts?.[0]?.type,
      displayText: data?.displayText?.trim() || undefined,
      successMessage: data?.successMessage?.trim() || undefined,
      message:
        discountAmount > 0 ? undefined : 'Kupon tidak berlaku untuk pesanan ini.',
      errorCode: discountAmount > 0 ? undefined : 'COUPON_NOT_APPLICABLE',
    };
  } catch (error) {
    const err = toApiError(error);
    if (err instanceof ApiError && err.code >= 400 && err.code < 500) {
      return {
        code,
        valid: false,
        message: err.message,
        errorCode: err.errorCode ?? undefined,
      };
    }
    throw err;
  }
}
